import { useEffect } from 'react';

interface SeoMetaProps {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType?: string;
  schema?: Record<string, unknown>;
}

export default function SeoMeta({ title, description, keywords, canonical, ogType = 'website', schema }: SeoMetaProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const metaMap: Record<string, string> = {
      description,
      keywords,
    };
    Object.entries(metaMap).forEach(([name, content]) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    });

    // Update og tags
    const ogMap: Record<string, string> = {
      'og:title': title,
      'og:description': description,
      'og:type': ogType,
      'og:url': canonical,
    };
    Object.entries(ogMap).forEach(([prop, content]) => {
      let el = document.querySelector(`meta[property="${prop}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', prop);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    });

    // Update twitter tags
    const twitterMap: Record<string, string> = {
      'twitter:title': title,
      'twitter:description': description,
    };
    Object.entries(twitterMap).forEach(([name, content]) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    });

    // Update canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical);

    // Inject JSON-LD schema
    if (schema) {
      const scriptId = 'json-ld-schema';
      let scriptEl = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = scriptId;
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(schema);
    }

    return () => {
      // Cleanup schema on unmount
      const scriptEl = document.getElementById('json-ld-schema');
      if (scriptEl) scriptEl.remove();
    };
  }, [title, description, keywords, canonical, ogType, schema]);

  return null;
}